import { useState, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateBreadcrumbSchema } from '@/components/SEO';
import { getCareers, getCareer } from '@/lib/api';
import { Link } from 'react-router-dom';
import { 
  MapPin, Clock, Building2, ArrowRight, ChevronRight, 
  X, CheckCircle, Briefcase, Users, Rocket 
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const perks = [
  {
    icon: Rocket,
    title: "Growth-First Culture",
    desc: "Work on cutting-edge projects and grow your skills rapidly.",
  },
  {
    icon: Users,
    title: "Collaborative Team",
    desc: "Join a team of passionate experts who support each other.",
  },
  {
    icon: Briefcase,
    title: "Flexible Work",
    desc: "Remote-friendly with flexible hours to suit your lifestyle.",
  },
];

export default function Careers() {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadCareers();
  }, []);

  const loadCareers = async () => {
    try {
      const res = await getCareers();
      setCareers(res.data);
    } catch (err) {
      console.error('Error loading careers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewJob = async (career) => {
    setSelectedJob(career);
    setDialogOpen(true);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'full-time': return 'bg-green-500/10 text-green-500';
      case 'part-time': return 'bg-blue-500/10 text-blue-500';
      case 'contract': return 'bg-yellow-500/10 text-yellow-500';
      case 'remote': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-[#ff5a1f]/10 text-[#ff5a1f]';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="careers-page">
      <SEO 
        title="Careers - Join Our Team"
        description="Join Wizbang and build the future of digital marketing. We're hiring passionate marketers, developers, and growth experts. Flexible work, growth culture."
        keywords="wizbang careers, digital marketing jobs, growth marketing jobs India, marketing agency jobs, remote marketing jobs"
        url="/careers"
        structuredData={generateBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Careers", path: "/careers" }
        ])}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-24 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <p className="text-[#ff5a1f] font-mono text-sm uppercase tracking-widest mb-6">
            // Careers
          </p>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 max-w-4xl">
            Build the Future
            <br />
            <span className="text-[#ff5a1f]">With Us</span>
          </h1>
          <p className="text-[#888] text-lg md:text-xl max-w-2xl leading-relaxed">
            Join a team of revenue engineers, growth hackers, and tech innovators. 
            We're always looking for passionate people to help brands scale.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="perks-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {perks.map((perk, idx) => (
              <div
                key={idx}
                className="p-6 border border-[#1F1F1F] hover:border-[#ff5a1f]/50 transition-all"
              >
                <div className="w-12 h-12 bg-[#ff5a1f]/10 flex items-center justify-center mb-4">
                  <perk.icon className="w-6 h-6 text-[#ff5a1f]" />
                </div>
                <h3 className="font-syne font-bold text-white mb-2">{perk.title}</h3>
                <p className="text-[#888] text-sm">{perk.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24" data-testid="positions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <p className="text-[#ff5a1f] font-mono text-sm uppercase tracking-widest mb-4">
              // Open Positions
            </p>
            <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">
              Current Openings
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-[#ff5a1f] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : careers.length > 0 ? (
            <div className="space-y-4">
              {careers.map((career) => (
                <div
                  key={career.id}
                  className="bg-[#0A0A0A] border border-[#1F1F1F] p-6 hover:border-[#ff5a1f]/50 transition-all cursor-pointer group"
                  onClick={() => handleViewJob(career)}
                  data-testid={`career-${career.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-syne text-xl font-bold text-white mb-2 group-hover:text-[#ff5a1f] transition-colors">
                        {career.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-[#888]">
                        <span className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {career.department}
                        </span>
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {career.location}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium uppercase ${getTypeColor(career.type)}`}>
                          {career.type}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[#ff5a1f] font-semibold text-sm group-hover:gap-3 transition-all">
                      View Details
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
              <Briefcase className="w-16 h-16 text-[#333] mx-auto mb-6" />
              <h3 className="font-syne text-xl font-bold text-white mb-3">
                No Open Positions Right Now
              </h3>
              <p className="text-[#888] mb-6">
                We're always looking for talented people. Send us your resume!
              </p>
              <a
                href="mailto:sayhi@wizbang.in"
                className="text-[#ff5a1f] font-semibold hover:underline"
              >
                sayhi@wizbang.in
              </a>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-3xl font-bold text-white mb-6">
            Don't See Your Role?
          </h2>
          <p className="text-[#888] text-lg mb-10">
            We're always open to exceptional talent. Drop us a line.
          </p>
          <a
            href="mailto:sayhi@wizbang.in"
            className="bg-[#ff5a1f] text-white px-10 py-5 text-sm font-semibold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors duration-300 btn-press inline-flex items-center gap-2"
          >
            Send Your Resume
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Job Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <DialogTitle className="font-syne text-2xl pr-8">
                  {selectedJob.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="mt-4 space-y-6">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-2 text-[#888]">
                    <Building2 className="w-4 h-4" />
                    {selectedJob.department}
                  </span>
                  <span className="flex items-center gap-2 text-[#888]">
                    <MapPin className="w-4 h-4" />
                    {selectedJob.location}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium uppercase ${getTypeColor(selectedJob.type)}`}>
                    {selectedJob.type}
                  </span>
                </div>

                <div>
                  <h4 className="font-syne font-bold text-white mb-3">Description</h4>
                  <p className="text-[#888] leading-relaxed whitespace-pre-wrap">
                    {selectedJob.description}
                  </p>
                </div>

                {selectedJob.requirements?.length > 0 && (
                  <div>
                    <h4 className="font-syne font-bold text-white mb-3">Requirements</h4>
                    <ul className="space-y-2">
                      {selectedJob.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-4 h-4 text-[#ff5a1f] flex-shrink-0 mt-1" />
                          <span className="text-[#888]">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-4 border-t border-[#1F1F1F]">
                  <a
                    href={`mailto:sayhi@wizbang.in?subject=Application for ${selectedJob.title}`}
                    className="w-full bg-[#ff5a1f] text-white px-6 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors flex items-center justify-center gap-2"
                  >
                    Apply Now
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
